import { useState, useEffect } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, FileText } from "lucide-react";
import { toast } from "sonner";

const inputCls = "w-full border border-gray-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]";
const labelCls = "block text-sm font-semibold text-gray-700 mb-1";

function downloadB64(b64, contentType, filename) {
  const bytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: contentType }));
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

export default function GenerateDocDialog({ open, onOpenChange, clientId, onGenerated }) {
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [name, setName] = useState("");
  const [body, setBody] = useState("");
  const [format, setFormat] = useState("pdf");
  const [saveToFile, setSaveToFile] = useState(true);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTemplateId(""); setName(""); setBody(""); setFormat("pdf"); setSaveToFile(true);
    api.get("/document-templates").then((r) => setTemplates(r.data)).catch(() => {});
  }, [open]);

  const pickTemplate = (id) => {
    setTemplateId(id);
    const t = templates.find((x) => x.id === id);
    if (t) { setName(t.name); setBody(t.body || ""); }
  };

  const generate = async () => {
    if (!templateId && !body.trim()) { toast.error("Pick a template or write a document body"); return; }
    setGenerating(true);
    try {
      const payload = { template_id: templateId || null, name, body, format, save_to_file: saveToFile };
      const { data } = await api.post(`/clients/${clientId}/generate-document`, payload);
      downloadB64(data.content_b64, data.content_type, data.filename);
      toast.success(data.saved ? "Document generated & saved to client file" : "Document generated");
      if (data.saved) onGenerated?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not generate document");
    } finally { setGenerating(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl rounded-sm max-h-[90vh] overflow-y-auto" data-testid="generate-doc-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold tracking-tight flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#002FA7]" /> Generate document
          </DialogTitle>
          <DialogDescription className="sr-only">Generate a document from a template for this client.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-1">
          <div>
            <label className={labelCls}>Template</label>
            <select data-testid="gen-template-select" value={templateId} onChange={(e) => pickTemplate(e.target.value)} className={inputCls}>
              <option value="">— Select a template —</option>
              {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Document title</label>
            <input data-testid="gen-name-input" value={name} onChange={(e) => setName(e.target.value)} className={inputCls} placeholder="e.g. Representation Letter" />
          </div>
          <div>
            <label className={labelCls}>Content <span className="font-normal text-gray-400">(merge fields fill in automatically)</span></label>
            <textarea data-testid="gen-body-input" value={body} onChange={(e) => setBody(e.target.value)} rows={9} className={`${inputCls} font-mono text-xs leading-relaxed`} />
          </div>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <span className={labelCls + " mb-0"}>Format:</span>
              {["pdf", "docx"].map((f) => (
                <button key={f} type="button" onClick={() => setFormat(f)} data-testid={`gen-format-${f}`}
                  className={`text-xs font-semibold px-3 py-1.5 rounded-sm border transition-colors ${format === f ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white text-gray-600 border-gray-200 hover:border-[#002FA7]"}`}>
                  {f.toUpperCase()}
                </button>
              ))}
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700" data-testid="gen-save-toggle">
              <input type="checkbox" checked={saveToFile} onChange={(e) => setSaveToFile(e.target.checked)} className="accent-[#002FA7]" />
              Save to client documents
            </label>
          </div>
        </div>
        <DialogFooter className="pt-2">
          <button onClick={() => onOpenChange(false)} className="bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 px-4 py-2 rounded-sm font-semibold text-sm">Cancel</button>
          <button onClick={generate} disabled={generating} data-testid="gen-generate-button"
            className="bg-[#002FA7] hover:bg-[#002480] text-white px-4 py-2 rounded-sm font-semibold text-sm flex items-center gap-2 disabled:opacity-60">
            {generating && <Loader2 className="w-4 h-4 animate-spin" />} Generate & download
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
